<template>
    <div class="flex flex-col items-center w-full h-full" :class="{ 'justify-center': isEditable }">
        <div class="grid grid-cols-4 gap-6 w-full mb-6" v-if="hasFile && displayItemCard">
            <activix-tooltip :content="file.name" :key="file.id" v-for="file in files">
                <div class="flex rounded shadow bg-white">
                    <div
                        class="flex justify-center items-center shrink-0 px-6 py-4 border-r border-gray-300 rounded-l bg-gray-200"
                    >
                        <icon :name="getIconName(file)" class="text-3xl text-gray-600" />
                    </div>

                    <div class="flex-1 px-3 py-4 overflow-x-hidden">
                        <p class="m-0 truncate">
                            {{ file.name }}
                        </p>
                        <p class="m-0 p-0 text-sm text-gray-600">{{ getFileSize(file) }} KB</p>
                    </div>

                    <div class="flex justify-center items-center pl-1 py-4 pr-6" v-if="isEditable">
                        <icon
                            class="text-xl text-gray-600 | cursor-pointer transition hover:text-red-700"
                            name="bold/delete-2"
                            @click="removeFile(file)"
                        />
                    </div>
                </div>
            </activix-tooltip>
        </div>

        <div
            class="w-full h-full"
            @drop.stop="dropFiles"
            @dragenter="onEnter"
            @dragleave="onLeave"
            v-if="isEditable"
        >
            <div
                class="flex flex-col items-center justify-center bg-white rounded border | transition duration-300"
                :class="[areaClasses, hoveringClasses]"
                v-if="!wrapper"
            >
                <icon
                    class="text-4xl"
                    name="regular/upload-thick-bottom"
                    :class="{ 'animate-bounce': isHovering }"
                />
                <span v-text="message" />

                <input
                    class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    :disabled="isUploading"
                    type="file"
                    :multiple="multiple"
                    :accept="acceptedFileTypes"
                    ref="uploadInput"
                    @change="fileChanged"
                    v-if="!wrapper"
                />
            </div>
            <slot v-if="wrapper" />
        </div>
    </div>
</template>

<script>
    import { isArray, concat } from 'lodash-es';

    const FILE_TYPES = [
        {
            name: 'XLSX',
            extension: 'xlsx',
            mimeTypes: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            icon: 'regular/office-file-xls',
        },
        {
            name: 'PDF',
            extension: 'pdf',
            mimeTypes: 'application/pdf',
            icon: 'regular/office-file-pdf',
        },
        {
            name: 'JPG',
            extension: 'jpg',
            mimeTypes: 'image/jpeg',
            icon: 'regular/image-file-landscape',
        },
        {
            name: 'JPEG',
            extension: 'jpeg',
            mimeTypes: 'image/jpeg',
            icon: 'regular/image-file-landscape',
        },
        {
            name: 'PNG',
            extension: 'png',
            mimeTypes: 'image/png',
            icon: 'regular/image-file-png',
        },
        {
            name: 'SVG',
            extension: 'svg',
            mimeTypes: 'image/svg+xml',
            icon: 'regular/image-file-svg',
        },
        {
            name: 'MP4',
            extension: 'mp4',
            mimeTypes: 'video/mp4',
            icon: 'regular/video-file-camera',
        },
        {
            name: 'MOV',
            extension: 'mov',
            mimeTypes: 'video/quicktime',
            icon: 'regular/video-file-camera',
        },
        {
            name: 'CSV',
            extension: 'csv',
            mimeTypes: 'text/csv',
            icon: 'regular/common-file-text',
        },
        {
            name: 'TXT',
            extension: 'text',
            mimeTypes: 'text/plain',
            icon: 'regular/common-file-text',
        },
    ];

    export default {
        compatConfig: {
            COMPONENT_V_MODEL: false,
        },

        emits: ['update:modelValue'],

        props: {
            modelValue: {
                type: Array,
                default: () => [],
            },
            accepts: {
                type: [String, Array],
                default: () => [
                    'image/*',
                    'text/plain',
                    '.pdf',
                    '.xls',
                    '.xlsx',
                    '.doc',
                    '.docx',
                    '.txt',
                    '.csv',
                    '.svg',
                    '.png',
                ],
            },
            description: {
                type: String,
                default: '',
            },
            isUploading: {
                type: Boolean,
                default: false,
            },
            isEditable: {
                type: Boolean,
                default: true,
            },
            multiple: {
                type: Boolean,
                default: true,
            },
            isAdvanced: {
                type: Boolean,
                default: true,
            },
            wrapper: {
                type: Boolean,
                default: false,
            },
            displayItemCard: {
                type: Boolean,
                default: true,
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            maximumSize: {
                type: Number,
                default: 0,
            },
            sizeUnit: {
                type: String,
                default: 'MB',
                validator: (value) => ['B', 'KB', 'MB', 'GB'].includes(value),
            },
        },

        data() {
            return {
                isHovering: false,
            };
        },

        computed: {
            message() {
                return this.description || this.$t('activixUi.upload.selectOrDrag');
            },

            areaClasses() {
                const height = this.isAdvanced ? 'h-56' : 'h-full';

                return this.wrapper
                    ? 'absolute z-20 opacity-0 inset-0 pointer-events-none'
                    : `relative space-y-3 w-full ${height}`;
            },

            hoveringClasses() {
                if (this.isHovering) {
                    return 'text-blue-500 border-blue-400';
                }

                return 'text-gray-600 border-gray-300 hover:text-blue-500 hover:border-blue-400';
            },

            acceptedFileTypes() {
                if (this.accepts instanceof Array) {
                    return this.accepts.join(',');
                }

                return this.accepts;
            },

            files() {
                return isArray(this.modelValue) ? this.modelValue : [this.modelValue];
            },

            hasFile() {
                return !!this.files.length;
            },

            sizeMultiplier() {
                // The base is always bytes
                switch (this.sizeUnit) {
                    case 'KB':
                        return 1000;
                    case 'MB':
                        return 1000 * 1000;
                    case 'GB':
                        return 1000 * 1000 * 1000;
                }

                return 1;
            },
        },

        methods: {
            dropFiles(event) {
                event.preventDefault();
                this.fileChanged(event);
            },

            fileChanged(event) {
                if (this.disabled) {
                    return;
                }

                this.isHovering = false;
                let files = null;

                if (event.target?.files?.length) {
                    files = event.target.files;
                }

                if (event.dataTransfer?.files?.length) {
                    files = event.dataTransfer.files;
                }

                if (!files?.length) {
                    return;
                }

                const nextFiles = concat(this.files, [...files]);

                if (!this.isSizeValid(nextFiles)) {
                    this.$notify.warning(
                        this.$t('activixUi.upload.fileTooBig', {
                            maximumSize: this.maximumSize + this.sizeUnit,
                        })
                    );

                    while (!this.isSizeValid(nextFiles)) {
                        nextFiles.pop();
                    }
                }

                this.$emit('update:modelValue', nextFiles);
            },

            removeFile(file) {
                if (this.isUploading) {
                    return;
                }

                this.$emit(
                    'update:modelValue',
                    this.files.filter((f) => f !== file)
                );
            },

            getFileType(file) {
                return FILE_TYPES.find((fileType) => {
                    return (
                        (Array.isArray(fileType.mimeTypes)
                            ? fileType.mimeTypes.includes(file.type)
                            : file.type === fileType.mimeTypes) ||
                        file.name.split('.').pop() === fileType.extension
                    );
                });
            },

            getIconName(file) {
                return this.getFileType(file)?.icon || 'regular/common-file-text';
            },

            getFileSize(file) {
                return Math.round(file.size / 1024);
            },

            onEnter(event) {
                event.preventDefault();

                if (this.disabled) {
                    return;
                }

                this.isHovering = true;
            },

            onLeave() {
                if (this.disabled) {
                    return;
                }

                this.isHovering = false;
            },

            isSizeValid(files = this.files) {
                const totalSize = files.reduce((accumulator, currentValue) => {
                    return accumulator + currentValue.size;
                }, 0);

                const allowedSize = this.maximumSize * this.sizeMultiplier;
                return !allowedSize || totalSize < allowedSize;
            },
        },
    };
</script>
